import React from "react";
import { Inbox, Paperclip, Eye } from "lucide-react";
import { fmtDate, fmtEmailTime } from "../../utils";
import type { SimEmail } from "../../types";

export interface FeedColProps {
  emails: SimEmail[];
  cursor: number;
  runState: "idle" | "running" | "paused" | "done";
  feedOpen: number | null;
  setFeedOpen: (id: number | null) => void;
  onView: (top: string) => void;
  /** Workstream the run actually routed this email to (live or replay result). */
  routedWs: (emailId: number) => string;
}

/* Left "Email Feed" column — shows the corpus in order, lets the user click
   into a row to see the raw message, and (after processing) jump to the
   email's spine card in the centre pane. */
export function FeedCol({ emails, cursor, runState, feedOpen, setFeedOpen, onView, routedWs }: FeedColProps): JSX.Element {
  const total = emails.length;
  return (
    <div className="scol">
      <div className="scolhd">
        <Inbox size={15} color="var(--muted)" />
        <span className="t">Email Feed</span>
        <span className="spacer" />
        <span className="mono tert" style={{ fontSize: 12 }}>
          {Math.min(cursor, total)}/{total}
        </span>
      </div>
      <div className="scolbody scroll">
        {emails.map((e, i) => {
          const done = i < cursor;
          const cur = i === cursor && runState !== "done";
          const open = feedOpen === e.id;
          const atts = e.attachments || [];
          return (
            <div
              key={e.id}
              className={"frow " + (cur ? "cur " : "")}
              style={{ opacity: done && !cur ? 0.62 : 1 }}
              onClick={() => setFeedOpen(open ? null : e.id)}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <div className="fsub">{e.subject}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
                  {atts.length > 0 && (
                    <span
                      className="mono"
                      title={atts.map((a) => a.filename).join("\n")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        fontSize: 11,
                        color: "var(--muted)",
                        border: "1px solid var(--bd2)",
                        borderRadius: 4,
                        padding: "1px 5px",
                      }}
                    >
                      <Paperclip size={10} />
                      {atts.length}
                    </span>
                  )}
                  <span
                    className="mono tert"
                    style={{ fontSize: 11, minWidth: 30, textAlign: "right" }}
                  >
                    #{e.id}
                  </span>
                </div>
              </div>
              <div className="fmeta">
                <span className="fsender">{e.fromName}</span>
                <span>{fmtDate((e.time || "").split(" · ")[0])}</span>
              </div>
              {open && (
                <div className="rawmail">
                  <div className="rawhdr">
                    <span className="rk">From</span>
                    <span className="rv">{e.from}</span>
                    <span className="rk">To</span>
                    <span className="rv">{(e.to || []).join(", ")}</span>
                    {(e.cc || []).length > 0 && (
                      <>
                        <span className="rk">Cc</span>
                        <span className="rv">{e.cc.join(", ")}</span>
                      </>
                    )}
                    <span className="rk">Date</span>
                    <span className="rv">{fmtEmailTime(e.time)}</span>
                    <span className="rk">Subj</span>
                    <span className="rv" style={{ color: "var(--fg)" }}>
                      {e.subject}
                    </span>
                  </div>
                  <div className="rawbody">
                    {(e.raw || "").split("\n").map((ln, k) =>
                      ln.startsWith(">") ? (
                        <div key={k} className="q">
                          {ln}
                        </div>
                      ) : (
                        <div key={k}>{ln || "\u00A0"}</div>
                      ),
                    )}
                  </div>
                  {done && (
                    <div style={{ marginTop: 9 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          onView(routedWs(e.id));
                        }}
                      >
                        <Eye size={13} /> View in spine
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
